import { useTranslation } from 'react-i18next';

import { getIn } from 'formik';
import styled from 'styled-components';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import { DELIVERY_DETAILS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { withStore } from '../../../../store';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import type { CardField, CardFieldComponent, CardFieldProps, CardStore } from '../../types';
import CopyShipTo from '../CopyShipTo';

const CheckboxWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const AppointmentRequiredWrapper = styled.div`
  width: 100%;
`;

const category = CARD_CATEGORIES.deliveryDetails;

function AppointmentRequired({ isReview, t, index = -1, fieldPrefix }: CardFieldProps) {
  const field = 'appointmentRequired';
  const name = `${fieldPrefix}.${field}`;

  const onCheckboxClicked = (formik: { form: { setFieldTouched: (field: string, touched: boolean) => void } }) =>
    setTimeout(() => formik.form.setFieldTouched(CARD_CATEGORIES.deliveryDetails, true));
  const config = {
    disabled: isReview,
    category,
    name,
    field,
    type: CONTROL_TYPE.checkbox,
    label: t(`shipTo.${category}.${field}.label`),
    onCheckboxClicked
  };

  return (
    <CheckboxWrapper>
      {/* Using this wrapper so that it aligns with the copy text */}
      <AppointmentRequiredWrapper>
        <Control {...config} />
      </AppointmentRequiredWrapper>
      {!isReview && index > -1 && <CopyShipTo index={index} category={category} />}
    </CheckboxWrapper>
  );
}

function ReceivingPlantContactName({ isReview, t, required, fieldPrefix }: CardFieldProps) {
  const field = 'receivingPlantContactName';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    required,
    validations: [{ type: VALIDATION.maxCharacters, param: 250 }],
    label: t(`shipTo.${category}.${field}.label`),
    type: '',
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function ReceivingPlantPhoneNumber({ isReview, t, required, fieldPrefix }: CardFieldProps) {
  const field = 'receivingPlantPhoneNumber';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    required,
    validations: [{ type: VALIDATION.maxCharacters, param: 40 }],
    label: t(`shipTo.${category}.${field}.label`),
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function AfterHoursContactName({ isReview, t, required, fieldPrefix }: CardFieldProps) {
  const field = 'afterHoursContactName';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    required,
    validations: [{ type: VALIDATION.maxCharacters, param: 250 }],
    label: t(`shipTo.${category}.${field}.label`),
    type: '',
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function AfterHoursPhoneNumber({ isReview, t, required, fieldPrefix }: CardFieldProps) {
  const field = 'afterHoursPhoneNumber';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    required,
    validations: [{ type: VALIDATION.maxCharacters, param: 40 }],
    label: t(`shipTo.${category}.${field}.label`),
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function WarehouseReceivingHours({ isReview, t, required, fieldPrefix }: CardFieldProps) {
  const field = 'warehouseReceivingHours';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    required,
    validations: [{ type: VALIDATION.maxCharacters, param: 250 }],
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

interface DeliveryDetailsComponentProps {
  isReview?: boolean;
  index?: number;
  store?: CardStore;
  fields: CardField[];
  fieldWrapper?: string;
}

function Component({ isReview, index = -1, store, fields, fieldWrapper }: DeliveryDetailsComponentProps) {
  const { t } = useTranslation();

  const cardProps = {
    cardId: category,
    title: t('shipTo.deliveryDetails.title'),
    subtitle: t('shipTo.deliveryDetails.subtitle'),
    image: '/food/onboarding/assets/Delivery_Details.svg',
    isReview,
    fieldWrapper,
    t
  };

  const controlMap: Record<string, CardFieldComponent> = {
    [DELIVERY_DETAILS_FIELDS.appointmentRequired]: AppointmentRequired,
    [DELIVERY_DETAILS_FIELDS.receivingPlantContactName]: ReceivingPlantContactName,
    [DELIVERY_DETAILS_FIELDS.receivingPlantPhoneNumber]: ReceivingPlantPhoneNumber,
    [DELIVERY_DETAILS_FIELDS.afterHoursContactName]: AfterHoursContactName,
    [DELIVERY_DETAILS_FIELDS.afterHoursPhoneNumber]: AfterHoursPhoneNumber,
    [DELIVERY_DETAILS_FIELDS.warehouseReceivingHours]: WarehouseReceivingHours
  };

  let wrapper = '';
  // allows for fields to be included on the root level, nested object, or nested array
  // fieldWrapper may already have an index baked in (e.g. 'shipTo[0]'), so don't append a second one
  const isAlreadyIndexed = /\]$/.test(fieldWrapper || '');
  if (fieldWrapper) {
    wrapper = index > -1 && !isAlreadyIndexed ? `${fieldWrapper}[${index}].` : `${fieldWrapper}.`;
  }

  const fieldPrefix = `${wrapper}${category}`;
  const areDetailsRequired = getIn(store.customer, `${fieldPrefix}.appointmentRequired`);

  return (
    <FormCard {...cardProps}>
      {fields.map(({ key, ...fieldProps }: CardField, index: number) => {
        const Component = controlMap[key];
        return (
          <Component
            t={t}
            isReview={isReview}
            index={index}
            fieldPrefix={fieldPrefix}
            required={areDetailsRequired}
            {...fieldProps}
            key={key}
          />
        );
      })}
    </FormCard>
  );
}

const DeliveryDetailsCard = withStore(Component);

export default DeliveryDetailsCard;
